import { calculateProgress } from './progress.js';
import type { ProjectState, ScopeChange } from './types.js';

// Object key order is not a scope change. Array order remains part of the plan.
function stable(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  if (value !== null && typeof value === 'object') return `{${Object.entries(value).filter(([, child]) => child !== undefined).sort(([a], [b]) => a.localeCompare(b)).map(([key, child]) => `${JSON.stringify(key)}:${stable(child)}`).join(',')}}`;
  return JSON.stringify(value) ?? 'null';
}

/** Builds a compact audit entry without retaining private PR payloads or source documents. */
export function createScopeChange(
  previous: ProjectState | null,
  next: ProjectState,
  details: Pick<ScopeChange, 'id' | 'kind' | 'createdAt' | 'reason' | 'actorId'>,
): ScopeChange | null {
  const before = previous ? calculateProgress(previous) : null;
  const after = calculateProgress(next);
  if (previous && stable(previous.scope) === stable(next.scope) && stable(before) === stable(after)) return null;
  const oldItems = new Map(previous?.scope.workItems.map(item => [item.id, item]) ?? []);
  const newItems = new Map(next.scope.workItems.map(item => [item.id, item]));
  const ids = [...newItems.keys()].sort();
  return {
    id: details.id, kind: details.kind, createdAt: details.createdAt, reason: details.reason,
    ...(details.actorId ? { actorId: details.actorId } : {}),
    revision: next.scope.revision, ...(previous ? { previousRevision: previous.scope.revision } : {}),
    added: ids.filter(id => !oldItems.has(id)),
    removed: [...oldItems.keys()].filter(id => !newItems.has(id)).sort(),
    updated: ids.filter(id => oldItems.has(id) && stable(oldItems.get(id)) !== stable(newItems.get(id))),
    reestimated: ids.filter(id => oldItems.has(id) && oldItems.get(id)!.effortPoints !== newItems.get(id)!.effortPoints),
    completed: ids.filter(id => after.workItems[id] === true && before?.workItems[id] !== true),
    reopened: ids.filter(id => after.workItems[id] === false && before?.workItems[id] === true),
    before, after,
  };
}
